import styled from "styled-components";
import { PageHeader } from "../components/visuals/PageHeader";
import { Panel } from "../components/visuals/Panel";
import { NextPicksTile } from "../tabs/Picks";
import { useEffect, useMemo, useState } from "react";
import { fetchEpisodeCastawayPickOptions, fetchEpisodeTribePickOptions } from "../api/picks";
import { Events, PickOptionsCastaway, PickOptionsTribe } from "../../../@types";
import { useQuery } from "react-query";
import { Loading } from "../components/elements/Loading";
import { getEvents } from "../api/events";
import { MultiSelect } from "../components/elements/MultiSelect/MultiSelect";
import { Button } from "../components/elements/Button";
import { updateEpisodeEvents } from "../api/admin";
const episodeId = 61;

export function AdminPage() {
  const { data: castawayOptions } = useQuery("castawayPickOption", () => fetchEpisodeCastawayPickOptions(episodeId));
  const { data: tribeOptions } = useQuery("tribePickOption", () => fetchEpisodeTribePickOptions(episodeId));

  const combinedData = useMemo<GroupedEventData[]>(() => {
    if (castawayOptions === undefined || tribeOptions === undefined) return [];
    return groupEventData(castawayOptions, tribeOptions);
  }, [castawayOptions, tribeOptions]);

  const { data: currentEvents } = useQuery(["CurrentEpisodeEvents", episodeId], () => getEvents(49, episodeId), {
    enabled: !!episodeId,
  });

  useEffect(() => {
    if (currentEvents) {
      const events = {};
      for (const event of currentEvents.castaway) {
        if (events[event.event_id] === undefined) events[event.event_id] = { castaways: [], tribes: [] };
        events[event.event_id].castaways.push(event.castaway_id);
      }
      for (const event of currentEvents.tribe) {
        if (events[event.event_id] === undefined) events[event.event_id] = { castaways: [], tribes: [] };
        events[event.event_id].tribes.push(event.game_tribe_id);
      }

      setUpdates(events);
    }
  }, [currentEvents]);

  const [updates, setUpdates] = useState<Updates>({});

  function onUpdate(event: { event_id: number; castaways: number[]; tribes: number[] }) {
    const { event_id, castaways, tribes } = event;
    const temp = { ...updates };
    temp[event_id] = { castaways, tribes };
    setUpdates(temp);
  }

  function onSave() {
    updateEpisodeEvents(episodeId, updates);
  }

  if (castawayOptions === undefined || tribeOptions === undefined || currentEvents === undefined) return <Loading />;

  return (
    <>
      <PageHeader title="Admin" />
      <EpisodeTileLayout>
        <EpisodeNumber style={{ padding: "10px" }}>Episode {episodeId}</EpisodeNumber>
        {combinedData.map((event) => (
          <EventSelect event={event} onUpdate={onUpdate} currentData={updates[event.event_id]} key={event.event_id} />
        ))}
        <Button onClick={onSave}>Save</Button>
      </EpisodeTileLayout>
    </>
  );
}

function EventSelect({
  event,
  currentData,
  onUpdate,
}: {
  event: GroupedEventData;
  currentData: Updates[number] | undefined;
  onUpdate: (event: { event_id: number; castaways: number[]; tribes: number[] }) => void;
}) {
  return (
    <NextPickEventElement key={event.name}>
      <EventName>
        <span>{event.name}:</span> {event.description}
      </EventName>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "0.5rem",
          width: "100%",
          marginTop: "0.5rem",
        }}
      >
        <MultiSelect
          width="100%"
          placeholder="Select Castaways"
          options={event.castaways}
          onChange={(val) =>
            onUpdate({
              event_id: event.event_id,
              castaways: val,
              tribes: [...(currentData?.tribes ?? [])],
            })
          }
          values={currentData?.castaways}
        />
        <MultiSelect
          width="100%"
          placeholder="Select Tribes"
          options={event.tribes}
          onChange={(val) =>
            onUpdate({
              event_id: event.event_id,
              castaways: [...(currentData?.castaways ?? [])],
              tribes: val,
            })
          }
          values={currentData?.tribes}
        />
      </div>
    </NextPickEventElement>
  );
}

type GroupedEventData = {
  name: string;
  event_id: number;
  description: string;
  castaways: {
    value: number;
    label: string;
  }[];
  tribes: {
    value: number;
    label: string;
  }[];
};

function groupEventData(castawayData: PickOptionsCastaway[], tribeData: PickOptionsTribe[]): GroupedEventData[] {
  const eventsMap: Record<number, GroupedEventData> = {};

  for (const row of castawayData) {
    if (!eventsMap[row.event_id]) {
      eventsMap[row.event_id] = {
        name: row.event_name,
        event_id: row.event_id,
        description: row.event_description,
        castaways: [],
        tribes: [],
      };
    }

    eventsMap[row.event_id]!.castaways.push({
      value: row.castaway_id,
      label: row.castaway_name,
    });
  }

  for (const row of tribeData) {
    if (!eventsMap[row.event_id]) {
      eventsMap[row.event_id] = {
        name: row.event_name,
        event_id: row.event_id,
        description: row.event_description,
        castaways: [],
        tribes: [],
      };
    }

    eventsMap[row.event_id]!.tribes.push({
      value: row.game_tribe_id,
      label: row.tribe_name,
    });
  }

  return Object.values(eventsMap);
}

type Updates = Record<string, { tribes: number[]; castaways: number[] }>;

const EpisodeTileLayout = styled(Panel)`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 95%;
  margin: auto;
  padding: 0;

  :last-of-type {
    margin-bottom: 3rem;
  }
`;

const EpisodeNumber = styled.h3`
  color: ${(props) => props.theme.panel.color};
  font-size: 22px;
  font-weight: 500;
  width: 100%;
  margin: 0;
`;

const EventElement = styled.div`
  padding: 10px;
  background: ${(props) => props.theme.panel.secondaryBackground};
  border-bottom: 2px solid ${(props) => props.theme.colors.main};
  color: ${(props) => props.theme.panel.color};

  :last-of-type {
    border-bottom: none;
    border-radius: 0 0 10px 10px;
  }
`;

const NextPickEventElement = styled(EventElement)`
  grid-template-rows: auto 1fr;
`;

const EventName = styled.p`
  grid-row: 1;
  grid-column: 1 / -1;
  font-size: 0.75rem;
  justify-self: start;
  margin-left: 10px;

  span {
    color: #000000;
  }
`;
